import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

export interface SelectOption {
  value: string;
  label: string;
}

interface EditableSelectCellProps {
  companyId: string;
  value?: string;
  label: string;
  options: SelectOption[];
  canEdit: boolean;
  formatValue?: (value: string) => string;
  onSave: (companyId: string, value: string) => Promise<void>;
}

const EditableSelectCell: React.FC<EditableSelectCellProps> = ({
  companyId,
  value,
  label,
  options,
  canEdit,
  formatValue,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedValue = value?.trim().toLowerCase() || '';
  const hasValue = Boolean(normalizedValue);

  const displayOptions = useMemo(() => {
    if (!hasValue || options.some((opt) => opt.value === normalizedValue)) {
      return options;
    }
    return [
      { value: normalizedValue, label: formatValue ? formatValue(normalizedValue) : normalizedValue },
      ...options,
    ];
  }, [hasValue, normalizedValue, options, formatValue]);

  const displayLabel = hasValue
    ? (formatValue ? formatValue(normalizedValue) : normalizedValue)
    : '—';

  const startEditing = (initial = '') => {
    setDraft(initial);
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const handleSave = async () => {
    if (!draft) {
      setError('Select a value');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, draft);
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box sx={{ mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
          <strong>{label}:</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} error={Boolean(error)}>
            <Select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              displayEmpty
              autoFocus
            >
              <MenuItem value="">
                <em>Select…</em>
              </MenuItem>
              {displayOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {error}
              </Typography>
            )}
          </FormControl>
          {saving ? (
            <CircularProgress size={18} sx={{ mt: 1, mx: 0.5 }} />
          ) : (
            <Box sx={{ display: 'flex', mt: 0.5 }}>
              <Tooltip title="Save">
                <IconButton size="small" color="success" onClick={handleSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelEditing}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>{label}:</strong> {displayLabel}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        <strong>{label}:</strong> {displayLabel}
      </Typography>
      {hasValue ? (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => startEditing(normalizedValue)}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => startEditing()}
          sx={{ fontSize: '0.8125rem', flexShrink: 0 }}
        >
          + Set status
        </Link>
      )}
    </Box>
  );
};

export default EditableSelectCell;
